<template>
    <div
        class="icon-btn-wrapper"
        :class="[wrapperClass, { 'icon-btn-wrapper--boxed': hasWrapperClass }]"
    >
        <ToolTip v-bind="tooltipProps">
            <template #tooltip-trigger>
                <button
                    ref="button"
                    :type="type"
                    :disabled="disabled"
                    :aria-label="computedAriaLabel"
                    :aria-pressed="pressed"
                    :class="['icon-btn', variantClass, sizeClass, $attrs.class]"
                    v-bind="otherAttrs"
                    @click="handleClick"
                >
                    <span v-if="showDot" class="notification-dot"></span>
                    <!-- SVG Icon -->
                    <img
                        v-if="computedSvgSrc"
                        :src="computedSvgSrc"
                        :alt="svgAlt"
                        class="icon-btn__svg"
                    />

                    <!-- Font Icon -->
                    <i
                        v-else-if="computedIconClass"
                        class="fi"
                        :class="computedIconClass"
                        aria-hidden="true"
                    ></i>
                </button>
            </template>

            <template #tooltip-content v-if="resolvedTooltip">
                {{ resolvedTooltip }}
            </template>
        </ToolTip>
    </div>
</template>

<script>
import ToolTip from '@/components/fields/ToolTip.vue';

// Star SVGs
// Star SVGs from src/assets/images/icons/
// star.svg
const STAR_SVG =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTUuMDkgOC4yNkwyMiA5LjI3TDE3IDE0LjE0TDE4LjE4IDIxLjAyTDEyIDE3Ljc3TDUuODIgMjEuMDJMNyAxNC4xNEwyIDkuMjdMOC45MSA4LjI2TDEyIDJaIiBzdHJva2U9IiNDMkMyQzIiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==';
// filled-star.svg
const STAR_FILLED_SVG =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTUuMDkgOC4yNkwyMiA5LjI3TDE3IDE0LjE0TDE4LjE4IDIxLjAyTDEyIDE3Ljc3TDUuODIgMjEuMDJMNyAxNC4xNEwyIDkuMjdMOC45MSA4LjI2TDEyIDJaIiBmaWxsPSIjRjBEQTlCIiBzdHJva2U9IiNGMERBOUIiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==';

const ICON_BUTTON_PRESETS = {
    info: {
        iconClass: 'fi-info',
        ariaLabel: 'Info',
        variant: 'light',
        size: 'default',
        tooltip: 'More info',
    },
    edit: {
        iconClass: 'fi-edit',
        ariaLabel: 'Edit',
        variant: 'white',
        size: 'default',
        tooltip: 'Edit',
    },
    delete: {
        iconClass: 'fi-trash',
        ariaLabel: 'Delete',
        variant: 'white',
        size: 'default',
        tooltip: 'Delete',
    },
    view: {
        iconClass: 'fi-eye',
        ariaLabel: 'View',
        variant: 'primary',
        size: 'default',
        tooltip: 'View',
    },
    add: {
        iconClass: 'fi-add',
        ariaLabel: 'Add',
        variant: 'primary',
        size: 'default',
        tooltip: 'Add',
    },
    expand: {
        iconClass: 'fi-chevron-down',
        ariaLabel: 'Expand',
        variant: 'white',
        size: 'default',
        tooltip: 'Expand',
    },
    collapse: {
        iconClass: 'fi-chevron-up',
        ariaLabel: 'Collapse',
        variant: 'white',
        size: 'default',
        tooltip: 'Collapse',
    },
    bell: {
        iconClass: 'fi-bell',
        ariaLabel: 'Notifications',
        variant: 'white',
        size: 'default',
        tooltip: 'Notifications',
    },
    chat: {
        iconClass: 'fi-chat',
        ariaLabel: 'Chat',
        variant: 'white',
        size: 'default',
        tooltip: 'Chat',
    },
    thread: {
        iconClass: 'fi-thread',
        ariaLabel: 'Chat',
        variant: 'white',
        size: 'default',
        tooltip: 'Chat',
    },
    check: {
        iconClass: 'fi-check',
        ariaLabel: 'Check',
        variant: 'white',
        size: 'default',
        tooltip: 'Check',
    },
    kebab: {
        iconClass: 'fi-kebab fi-rotate-90',
        ariaLabel: 'Menu',
        variant: 'white',
        size: 'default',
    },
    copy: {
        iconClass: 'fi-copy',
        ariaLabel: 'Copy',
        variant: 'white',
        size: 'default',
        tooltip: 'Copy',
    },
    download: {
        iconClass: 'fi-download',
        ariaLabel: 'Download',
        variant: 'white',
        size: 'default',
        tooltip: 'Download',
    },
    filter: {
        iconClass: 'fi-filter',
        ariaLabel: 'Filter',
        variant: 'white',
        size: 'default',
        tooltip: 'Filter',
    },

    // SVG-based presets
    star: {
        svgSrc: STAR_SVG,
        svgAlt: '',
        ariaLabel: 'Add to favourites',
        variant: 'light',
        size: 'default',
        tooltip: 'Add to favourites',
    },
    'star-filled': {
        svgSrc: STAR_FILLED_SVG,
        svgAlt: '',
        ariaLabel: 'Remove from favourites',
        variant: 'light',
        size: 'default',
        tooltip: 'Remove from favourites',
    },
};

export default {
    name: 'IconButton',
    inheritAttrs: false,
    components: {
        ToolTip,
    },
    props: {
        // Behavior / identity
        preset: {
            type: String,
            default: null,
        },

        // Icon source
        iconClass: {
            type: String,
            default: '',
        },
        svgSrc: {
            type: String,
            default: '',
        },
        svgAlt: {
            type: String,
            default: '',
        },

        // Accessibility + semantics
        ariaLabel: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        pressed: {
            type: Boolean,
            default: null,
        },

        // Button behavior
        type: {
            type: String,
            default: 'button',
        },
        disabled: {
            type: Boolean,
            default: false,
        },

        // Styling
        variant: {
            type: String,
            default: undefined,
            validator: (v) => ['primary', 'white', 'light'].includes(v),
        },
        size: {
            type: String,
            default: undefined,
            validator: (v) => ['default', 'lg', 'normal'].includes(v),
        },

        // Tooltip
        tooltip: {
            type: [String, Boolean, null],
            default: undefined,
        },
        tooltipProps: {
            type: Object,
            default: () => ({}),
        },
        // Use `wrapperClass` for outer layout/alignment. Normal `class` is applied
        // to the inner <button> and should be used for button size/state styling.
        wrapperClass: {
            type: [String, Array, Object],
            default: '',
        },
        showTooltipWhenDisabled: {
            type: Boolean,
            default: true,
        },
        showDot: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click'],
    computed: {
        activePreset() {
            return this.preset && ICON_BUTTON_PRESETS[this.preset]
                ? ICON_BUTTON_PRESETS[this.preset]
                : {};
        },
        computedIconClass() {
            return this.iconClass || this.activePreset.iconClass || '';
        },
        computedSvgSrc() {
            return this.svgSrc || this.activePreset.svgSrc || '';
        },
        computedAriaLabel() {
            return (
                this.ariaLabel ||
                this.activePreset.ariaLabel ||
                this.label ||
                'Button'
            );
        },
        variantClass() {
            // Priority: prop > preset > default('white')
            const v = this.variant || this.activePreset.variant || 'white';
            return `icon-btn-${v}`;
        },
        sizeClass() {
            // Priority: prop > preset > default('normal')
            // 'default' and 'normal' result in no suffix. 'lg' results in icon-btn-lg.
            const s = this.size || this.activePreset.size || 'normal';
            if (s === 'lg') return 'icon-btn-lg';
            return '';
        },
        resolvedTooltip() {
            if (
                this.tooltip === false ||
                this.tooltip === null ||
                this.tooltip === ''
            ) {
                return null;
            }
            if (this.tooltip === true) {
                return this.activePreset.tooltip || null;
            }
            if (typeof this.tooltip === 'string') {
                return this.tooltip;
            }
            return this.activePreset.tooltip;
        },
        hasWrapperClass() {
            if (Array.isArray(this.wrapperClass)) {
                return this.wrapperClass.length > 0;
            }
            if (this.wrapperClass && typeof this.wrapperClass === 'object') {
                return Object.values(this.wrapperClass).some(Boolean);
            }
            return Boolean(this.wrapperClass);
        },
        otherAttrs() {
            // Exclude `class` from $attrs because classes are applied separately via the component's
            // explicit class bindings; only non-class attributes should be forwarded here.
            const { class: _class, ...attrs } = this.$attrs;
            return attrs;
        },
    },
    methods: {
        getTriggerElement() {
            return this.$refs.button || null;
        },
        handleClick(event) {
            if (!this.disabled) {
                this.$emit('click', event);
            }
        },
    },
};
</script>

<style scoped>
.icon-btn-wrapper {
    display: contents;
}

.icon-btn-wrapper--boxed {
    display: block;
}

/* Ensure SVG scales correctly if not already handled */
.icon-btn {
    position: relative;
}

.icon-btn__svg {
    width: 1.125rem;
    height: 1.125rem;
    display: block; /* Removes inline spacing */
}

/* Add aria-pressed styling to match active state if not present globally */
.icon-btn[aria-pressed='true'] {
    background-color: var(--neutral-900);
    color: var(--white);
    border-color: var(--neutral-900);
}

.notification-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    background-color: var(--danger);
    border: 2px solid var(--white);
    border-radius: 50%;
}
</style>
